// Theme tokens, verbatim from docs/API_CONTRACT.md ("Themes" section, plus its "Accessible
// semantic tokens" subsection for onPrimary/accentText/secondaryText/mutedText/focusRing).
// Do NOT change these hex values without updating the contract doc first -- the sibling mobile
// app is built from the same source of truth and must render identical colors.
//
// `themePreference` is a direct user choice (Settings), independent of `traditions[]`.
// Traditions only drive content filtering (quotes/practices/library) -- never color.

export type ThemeId = 'saffron' | 'indigo' | 'lotus' | 'sage' | 'amethyst';

export type Tradition =
  // Major Religions
  | 'christian'
  | 'catholic'
  | 'protestant'
  | 'orthodox_christian'
  | 'islamic'
  | 'sunni'
  | 'shia'
  | 'hindu'
  | 'vaishnavism'
  | 'shaivism'
  | 'shaktism'
  | 'buddhist'
  | 'theravada'
  | 'mahayana'
  | 'vajrayana'
  | 'chinese_folk'
  | 'taoist'
  | 'religious_taoism'
  | 'philosophical_taoism'
  | 'jewish'
  | 'orthodox_judaism'
  | 'conservative_judaism'
  | 'reform_judaism'
  // Alternative Practices
  | 'crystal_healing'
  | 'sound_bath'
  | 'aromatherapy'
  | 'chromotherapy'
  | 'somatic_mindfulness'
  | 'earthing'
  | 'breathwork'
  | 'qi_gong'
  | 'floatation'
  | 'stoic';

export type TraditionGroup = 'Major Religions' | 'Alternative Practices';

export const TRADITION_GROUPS: Record<TraditionGroup, Tradition[]> = {
  'Major Religions': [
    'christian', 'islamic', 'hindu', 'buddhist', 'chinese_folk',
    'taoist', 'jewish',
  ],
  'Alternative Practices': [
    'crystal_healing', 'sound_bath', 'aromatherapy',
    'chromotherapy', 'somatic_mindfulness', 'earthing', 'breathwork',
    'qi_gong', 'floatation', 'stoic',
  ],
};

// Denominations/schools within a parent tradition -- selectable in their own right (for finer
// content personalization) alongside the parent. Only listed here where a genuinely well-known,
// widely-recognized subdivision exists; not every tradition has one.
export const TRADITION_SUBCATEGORIES: Partial<Record<Tradition, Tradition[]>> = {
  christian: ['catholic', 'protestant', 'orthodox_christian'],
  islamic: ['sunni', 'shia'],
  hindu: ['vaishnavism', 'shaivism', 'shaktism'],
  buddhist: ['theravada', 'mahayana', 'vajrayana'],
  taoist: ['religious_taoism', 'philosophical_taoism'],
  jewish: ['orthodox_judaism', 'conservative_judaism', 'reform_judaism'],
};

// Reverse of TRADITION_SUBCATEGORIES -- a subcategory's parent, for content filtering (selecting
// a denomination should still surface the parent's general content, not just its own).
export const TRADITION_PARENTS: Partial<Record<Tradition, Tradition>> = Object.fromEntries(
  Object.entries(TRADITION_SUBCATEGORIES).flatMap(([parent, subs]) =>
    (subs ?? []).map((sub) => [sub, parent as Tradition])
  )
) as Partial<Record<Tradition, Tradition>>;

/** Every top-level tradition has its own male/female costumed mascot art under
 * public/mascot/{tradition}-{gender}.jpg. A subcategory (e.g. `catholic`) has no art of its
 * own -- it wears its parent's outfit (`christian`). */
export function mascotImagePath(tradition: Tradition | null | undefined, gender: 'male' | 'female' | null | undefined): string {
  const g = gender === 'female' ? 'female' : 'male';
  if (!tradition) return `/mascot-${g}.jpg`;
  const resolved = TRADITION_PARENTS[tradition] ?? tradition;
  return `/mascot/${resolved}-${g}.jpg`;
}

export const ALL_TRADITIONS: Tradition[] = [
  ...TRADITION_GROUPS['Major Religions'],
  ...TRADITION_GROUPS['Alternative Practices'],
  ...Object.values(TRADITION_SUBCATEGORIES).flat(),
];

export interface ThemeTokens {
  primary: string;
  secondary: string;
  background: string;
  surface: string;
  text: string;
  muted: string;
  pillBg: string;
  // --- Accessible semantic tokens (review fix F7, 2026-09-10) ------------------------------
  // The seven values above are the "Five Paths, One Palette" design-study colors, kept exactly
  // as-is. Several fail WCAG contrast when used as TEXT (e.g. saffron's gold primary is 2.46:1 on
  // its own background), so text roles get their own hue-preserving variants. Ratios are computed
  // and enforced by scripts/check_theme_contrast.py (also run by the backend test suite as
  // tests/test_theme_contrast.py) -- change a value only with that check passing.
  /** Text/icons drawn ON a `primary` fill (buttons, selected chips). >= 4.5:1 vs primary. */
  onPrimary: string;
  /** Primary-hued text (links, active tab/nav item, eyebrows). >= 4.5:1 on background/surface/pillBg. */
  accentText: string;
  /** Secondary-hued text (streak pill, tags, notices). >= 4.5:1 on background/surface/pillBg. */
  secondaryText: string;
  /** Muted body text (subtitles, labels, meta). >= 4.5:1 on background/surface/pillBg. */
  mutedText: string;
  /** Keyboard focus outline. >= 3:1 (non-text contrast) against background/surface/pillBg. */
  focusRing: string;
}

export const ALL_THEME_IDS: ThemeId[] = ['saffron', 'indigo', 'lotus', 'sage', 'amethyst'];

export const DEFAULT_THEME_ID: ThemeId = 'saffron';

export const THEMES: Record<ThemeId, ThemeTokens> = {
  saffron: {
    primary: '#C77D2E',
    secondary: '#7A2E2E',
    background: '#EFDDBB',
    surface: '#FFF8EA',
    text: '#2B1B0E',
    muted: '#8A6F4E',
    pillBg: '#F7E7C8',
    onPrimary: '#2B1B0E',
    accentText: '#895620',
    secondaryText: '#7A2E2E',
    mutedText: '#735D41',
    focusRing: '#7A2E2E',
  },
  indigo: {
    primary: '#2E2A6E',
    secondary: '#B9A6F0',
    background: '#F4F3FA',
    surface: '#FFFFFF',
    text: '#1B1830',
    muted: '#7D7A99',
    pillBg: '#E4DEF7',
    onPrimary: '#FFFFFF',
    accentText: '#2E2A6E',
    secondaryText: '#6951AC',
    mutedText: '#615E7A',
    focusRing: '#6951AC',
  },
  lotus: {
    primary: '#4E7C5C',
    secondary: '#E8A7B0',
    background: '#F1F6F0',
    surface: '#FFFFFF',
    text: '#1F2A22',
    muted: '#7D9284',
    pillBg: '#F6DDE0',
    onPrimary: '#FFFFFF',
    accentText: '#446C50',
    secondaryText: '#9F4754',
    mutedText: '#56675C',
    focusRing: '#9F4754',
  },
  sage: {
    primary: '#7C8C6B',
    secondary: '#C1653D',
    background: '#EFEDE5',
    surface: '#FFFFFF',
    text: '#2A2A22',
    muted: '#948C78',
    pillBg: '#EAD9CD',
    onPrimary: '#161612',
    accentText: '#57624B',
    secondaryText: '#924D2E',
    mutedText: '#655F50',
    focusRing: '#924D2E',
  },
  amethyst: {
    primary: '#6E4A9E',
    secondary: '#B8A98C',
    background: '#F6F3F9',
    surface: '#FFFFFF',
    text: '#241B33',
    muted: '#8C81A3',
    pillBg: '#E9DFF0',
    onPrimary: '#FFFFFF',
    accentText: '#6E4A9E',
    secondaryText: '#6B624F',
    mutedText: '#695E81',
    focusRing: '#6B624F',
  },
};

// Human-readable display names for the Settings theme picker ("Five Paths, One Palette" study).
export const THEME_DISPLAY_NAMES: Record<ThemeId, string> = {
  saffron: 'Saffron & Sacred Gold',
  indigo: 'Indigo Night & Starlight',
  lotus: 'Lotus & Jade',
  sage: 'Desert Sage & Terracotta',
  amethyst: 'Amethyst & Moonstone',
};

export function isValidTheme(value: string): value is ThemeId {
  return (ALL_THEME_IDS as string[]).includes(value);
}

// Human-readable labels for the tradition picker (content filtering only -- not theming).
export const TRADITION_LABELS: Record<Tradition, string> = {
  christian: 'Christian',
  catholic: 'Catholic',
  protestant: 'Protestant',
  orthodox_christian: 'Orthodox Christian',
  islamic: 'Islamic',
  sunni: 'Sunni',
  shia: 'Shia',
  hindu: 'Hindu',
  vaishnavism: 'Vaishnavism',
  shaivism: 'Shaivism',
  shaktism: 'Shaktism',
  buddhist: 'Buddhist',
  theravada: 'Theravada',
  mahayana: 'Mahayana',
  vajrayana: 'Vajrayana',
  chinese_folk: 'Chinese Folk & Daoism',
  taoist: 'Taoist',
  religious_taoism: 'Religious Taoism',
  philosophical_taoism: 'Philosophical Taoism',
  jewish: 'Jewish',
  orthodox_judaism: 'Orthodox Judaism',
  conservative_judaism: 'Conservative Judaism',
  reform_judaism: 'Reform Judaism',
  crystal_healing: 'Healing Crystals',
  sound_bath: 'Sound Bath Therapy',
  aromatherapy: 'Aromatherapy',
  chromotherapy: 'Chromotherapy',
  somatic_mindfulness: 'Somatic Mindfulness',
  earthing: 'Earthing / Grounding',
  breathwork: 'Breathwork',
  qi_gong: 'Qi Gong & Tai Chi',
  floatation: 'Floatation Therapy',
  stoic: 'Stoic',
};

// CSS custom property names written onto the root element by ThemeProvider.
//
// Review fix F7: across global.css, `--color-muted` and `--color-secondary` are (almost) only ever
// used as TEXT colors, so those two variable names now carry the accessible text variants
// (`mutedText`, `secondaryText`) -- every existing and future `color: var(--color-muted)` stays
// readable without editing each rule. The raw design-study hexes remain available as
// `--color-muted-raw` / `--color-secondary-raw` for decorative use. `--color-primary` is used for
// fills as well as text, so primary-hued TEXT uses the separate `--color-accent-text`, and text on
// a primary fill uses `--color-on-primary`.
export const CSS_VAR_NAMES: Record<keyof ThemeTokens, string> = {
  primary: '--color-primary',
  secondary: '--color-secondary-raw',
  background: '--color-background',
  surface: '--color-surface',
  text: '--color-text',
  muted: '--color-muted-raw',
  pillBg: '--color-pill-bg',
  onPrimary: '--color-on-primary',
  accentText: '--color-accent-text',
  secondaryText: '--color-secondary',
  mutedText: '--color-muted',
  focusRing: '--color-focus',
};
